import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { alertError, alertSuccess } from '../../../components/Toast';
import { loadBanks, saveBank, deleteBank } from '../../../core/utils/bank';
import FinancialListModal from './FinancialListModal';
import BankSummary from './BankSummary';
import BankTable from './BankTable';
import BankFormModal from './BankFormModal';

const INITIAL_FORM = {
  code: '',
  name: '',
  agency: '',
  account: '',
  account_type: 'corrente',
  holder: '',
  balance: '',
  status: 'ativo',
  observation: '',
};

const BankModal = ({ isOpen, onClose, onChanged }) => {
  const company = useSelector((state) => state.app.company || {});
  const user = useSelector((state) => state.app.user || {});
  const [banks, setBanks] = useState([]);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setSearch('');
  }, [isOpen]);

  const fetchBanks = async () => {
    const data = await loadBanks(company.id);
    setBanks(data);
  };

  useEffect(() => {
    if (isOpen && company.id) fetchBanks();
  }, [isOpen, company.id]);

  const filtered = banks.filter((b) => {
    const term = search.toLowerCase();
    return (
      b.name?.toLowerCase().includes(term) ||
      b.agency?.toLowerCase().includes(term) ||
      b.code?.toLowerCase().includes(term)
    );
  });

  const openForm = (bank = null) => {
    if (bank) {
      setForm({
        code: bank.code || '',
        name: bank.name || '',
        agency: bank.agency || '',
        account: bank.account || '',
        account_type: bank.account_type || 'corrente',
        holder: bank.holder || '',
        balance: bank.balance != null ? String(bank.balance) : '',
        status: bank.status || 'ativo',
        observation: bank.observation || '',
      });
      setEditingId(bank.id);
    } else {
      setForm(INITIAL_FORM);
      setEditingId(null);
    }
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setForm(INITIAL_FORM);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (saving) return;

    if (!form.name.trim()) {
      alertError('Nome do banco é obrigatório.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        balance: Number(String(form.balance).replace(/[^\d,-]/g, '').replace(',', '.')) || 0,
        company_id: company.id,
        created_by: user.id,
      };

      const { error } = await saveBank(editingId, payload);
      if (error) {
        alertError('Erro ao salvar banco.');
        return;
      }

      alertSuccess(editingId ? 'Banco atualizado!' : 'Banco cadastrado!');
      closeForm();
      fetchBanks();
      if (onChanged) onChanged();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bank, onSuccess) => {
    const result = await Swal.fire({
      title: 'Deseja excluir este banco?',
      text: bank.name,
      icon: 'warning',
      showCancelButton: true,
      showConfirmButton: false,
      showDenyButton: true,
      denyButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
    });

    if (result.isDenied) {
      const { error } = await deleteBank(bank.id, user.id);
      if (!error) {
        alertSuccess('Banco removido!');
        fetchBanks();
        if (onChanged) onChanged();
        if (onSuccess) onSuccess();
      } else {
        alertError('Erro ao remover banco.');
      }
    }
  };

  return (
    <FinancialListModal
      isOpen={isOpen}
      onClose={onClose}
      title="Cadastro de Bancos"
      badge={banks.length}
      searchPlaceholder="Buscar banco ou agência..."
      search={search}
      onSearchChange={setSearch}
      onNew={() => openForm()}
      newLabel="+ Novo Banco"
    >
      <BankSummary banks={banks} />

      <BankTable banks={filtered} onEdit={openForm} />

      {formOpen && (
        <BankFormModal
          form={form}
          setForm={setForm}
          editingId={editingId}
          onSave={handleSave}
          onClose={closeForm}
          onDelete={editingId ? () => {
            const bank = banks.find((b) => b.id === editingId);
            if (bank) handleDelete(bank, closeForm);
          } : undefined}
          saving={saving}
        />
      )}
    </FinancialListModal>
  );
};

export default BankModal;
